// AI Joomla SEO Utilities
// Comprehensive SEO management for all pages

const SITE_NAME = 'AI Joomla';
const SITE_URL = 'https://aijoomla.org';
const DEFAULT_IMAGE = '/favicon.png';
const TWITTER_HANDLE = '';

// Helper to set or create meta tags
const setOrCreateMeta = (selector, attributes, content) => {
  if (!content) return;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

// Helper to set or create link tags
const setOrCreateLink = (rel, href, attributes = {}) => {
  if (!href) return;
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

// Main SEO function
export const setSeoMetadata = ({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  ogImage,
  twitterCard = 'summary_large_image',
  jsonLd,
  noIndex = false,
  author,
}) => {
  // Title
  if (title) {
    document.title = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  }

  // Basic meta tags
  setOrCreateMeta('meta[name="description"]', { name: 'description' }, description);
  setOrCreateMeta('meta[name="keywords"]', { name: 'keywords' }, keywords);
  setOrCreateMeta('meta[name="author"]', { name: 'author' }, author || SITE_NAME);
  
  // Robots
  setOrCreateMeta(
    'meta[name="robots"]', 
    { name: 'robots' }, 
    noIndex ? 'noindex, nofollow' : 'index, follow'
  );

  // Open Graph
  setOrCreateMeta('meta[property="og:title"]', { property: 'og:title' }, title);
  setOrCreateMeta('meta[property="og:description"]', { property: 'og:description' }, description);
  setOrCreateMeta('meta[property="og:type"]', { property: 'og:type' }, ogType);
  setOrCreateMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, SITE_NAME);
  setOrCreateMeta('meta[property="og:image"]', { property: 'og:image' }, ogImage || DEFAULT_IMAGE);
  setOrCreateMeta('meta[property="og:locale"]', { property: 'og:locale' }, 'en_US');
  
  if (canonicalPath) {
    const fullUrl = `${SITE_URL}${canonicalPath}`;
    setOrCreateMeta('meta[property="og:url"]', { property: 'og:url' }, fullUrl);
  }

  // Twitter Card
  setOrCreateMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, twitterCard);
  setOrCreateMeta('meta[name="twitter:site"]', { name: 'twitter:site' }, TWITTER_HANDLE);
  setOrCreateMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
  setOrCreateMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
  setOrCreateMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, ogImage || DEFAULT_IMAGE);

  // Canonical URL
  if (canonicalPath) {
    setOrCreateLink('canonical', `${SITE_URL}${canonicalPath}`);
  }

  // JSON-LD Structured Data
  if (jsonLd) {
    // Remove existing JSON-LD
    const existingJsonLd = document.getElementById('page-json-ld');
    if (existingJsonLd) {
      existingJsonLd.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'page-json-ld';
    script.text = JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd]);
    document.head.appendChild(script);
  }
};

// Pre-built JSON-LD generators
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}${DEFAULT_IMAGE}`,
  description: 'AI Joomla builds premium Joomla 4, 5 & 6 extensions — components, modules, plugins, templates, and a fully comprehensive security suite — engineered in-house by A2G Designs.',
  telephone: '+1-888-990-0440',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'US',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-888-990-0440',
    contactType: 'sales',
    email: 'info@aijoomla.org',
  },
});

export const generateLocalBusinessSchema = (location = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}${DEFAULT_IMAGE}`,
  image: `${SITE_URL}${DEFAULT_IMAGE}`,
  description: 'AI Joomla builds premium Joomla 4, 5 & 6 extensions engineered in-house by A2G Designs.',
  telephone: '+1-888-990-0440',
  address: {
    '@type': 'PostalAddress',
    streetAddress: location.address || '',
    addressLocality: location.city || '',
    addressRegion: location.state || '',
    postalCode: location.zip || '',
    addressCountry: 'US',
  },
  geo: location.lat && location.lng ? {
    '@type': 'GeoCoordinates',
    latitude: location.lat,
    longitude: location.lng,
  } : undefined,
  priceRange: '$$$',
});

export const generateProductSchema = (product) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  image: product.images?.[0] || DEFAULT_IMAGE,
  sku: product.sku || product.id,
  brand: {
    '@type': 'Brand',
    name: product.brand || SITE_NAME,
  },
  offers: {
    '@type': 'Offer',
    url: `${SITE_URL}/shop/products/${product.slug || product.id}`,
    priceCurrency: 'USD',
    price: product.price,
    availability: product.in_stock !== false 
      ? 'https://schema.org/InStock' 
      : 'https://schema.org/OutOfStock',
    seller: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
  },
});

export const generateArticleSchema = (article) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.summary || article.description,
  image: article.image || DEFAULT_IMAGE,
  datePublished: article.published_date,
  dateModified: article.updated_date || article.published_date,
  author: {
    '@type': 'Organization',
    name: SITE_NAME,
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}${DEFAULT_IMAGE}`,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SITE_URL}/research/${article.slug}`,
  },
});

export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url ? `${SITE_URL}${item.url}` : undefined,
  })),
});

export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const generateWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/shop?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

// SEO presets for common pages
export const SEO_PRESETS = {
  home: {
    title: 'AI Joomla — Premium Joomla Extensions, Components, Modules, Plugins & Templates',
    description: 'AI Joomla builds premium Joomla 4, 5 & 6 extensions — components, modules, plugins and templates — plus custom extensions built to your exact spec.',
    keywords: 'Joomla extensions, Joomla components, Joomla modules, Joomla plugins, Joomla templates, custom Joomla extension, Joomla 5, Joomla 6, AI Joomla',
    canonicalPath: '/',
    ogType: 'website',
  },
  shop: {
    title: 'Shop Joomla Extensions',
    description: 'Browse our catalog of premium Joomla components, modules, plugins, and templates — engineered in-house for Joomla 4, 5 & 6.',
    keywords: 'Joomla extensions shop, Joomla components, Joomla modules, Joomla plugins, Joomla templates',
    canonicalPath: '/shop',
    ogType: 'website',
  },
  about: {
    title: 'About Us',
    description: 'AI Joomla is built and maintained by A2G Designs — a Tampa, Florida based agency with 20+ years of experience in web design, full stack software development, and hosting.',
    keywords: 'about AI Joomla, A2G Designs, Joomla extension developers',
    canonicalPath: '/about',
    ogType: 'website',
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with AI Joomla. We\'re here to help with your Joomla extension needs. Call (888) 990-0440.',
    keywords: 'contact AI Joomla, customer support, Joomla extension support, sales inquiry',
    canonicalPath: '/contact',
    ogType: 'website',
  },
  faq: {
    title: 'Frequently Asked Questions',
    description: 'Find answers to common questions about our Joomla extensions, licensing, updates, and support.',
    keywords: 'FAQ, Joomla extension questions, licensing help, support',
    canonicalPath: '/faq',
    ogType: 'website',
  },
};

// Export site constants for use elsewhere
export { SITE_NAME, SITE_URL, DEFAULT_IMAGE, TWITTER_HANDLE };
